import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MessageSquare, Phone, Send, X } from 'lucide-react';
import { company } from '../data/site';
type Message = {
  id: number;
  from: 'bot' | 'user';
  text: string;
};
const quickReplies = ['I need an estimate', 'I have a project question', 'I want to see your work', "I'd like to speak with someone"];
function replyFor(input: string): string {
  const q = input.toLowerCase();
  if (q.includes('estimate') || q.includes('quote') || q.includes('price') || q.includes('cost') || q.includes('budget')) {
    return `Estimates are always free. Share your project type, rough size, and your city — or scroll to the estimate form and we'll respond within one business day. Need it faster? Call ${company.phone}.`;
  }
  if (q.includes('speak') || q.includes('call') || q.includes('someone') || q.includes('talk')) {
    return `Happy to talk it through. Call or text ${company.phone} — we're available ${company.hours}. You can also email ${company.email}.`;
  }
  if (q.includes('work') || q.includes('portfolio') || q.includes('project') || q.includes('photo')) {
    return 'Our Work section has recent builds — concrete, decks, remodels, additions, and holiday lighting. Tap any project to see the scope and photos.';
  }
  if (q.includes('concrete') || q.includes('driveway') || q.includes('patio') || q.includes('masonry')) {
    return 'We handle driveways, patios, slabs, foundations, retaining walls, and stonework. Send the approximate square footage and we can give you a ballpark before we come out.';
  }
  if (q.includes('deck') || q.includes('fence') || q.includes('rail')) {
    return 'We build custom decks, railings, and fences in wood and composite. Let us know the size and material and we will put together an itemized quote.';
  }
  if (q.includes('remodel') || q.includes('renovat') || q.includes('kitchen') || q.includes('bath') || q.includes('addition')) {
    return 'Remodels, renovations, and additions are a core part of what we do — from a single room to a full interior re-plan. Tell us what you have in mind and we will walk the space with you.';
  }
  if (q.includes('holiday') || q.includes('light') || q.includes('christmas')) {
    return 'Holiday lighting includes design, install, season-long maintenance, and takedown. Spots fill up fast, so reach out early to get on the calendar.';
  }
  if (q.includes('area') || q.includes('serve') || q.includes('where') || q.includes('locat')) {
    return `We serve Nashville, Murfreesboro, Smyrna, Franklin, Brentwood, La Vergne, Antioch, and Mt. Juliet. Our office is at ${company.addressLine1}, ${company.addressLine2}.`;
  }
  if (q.includes('licens') || q.includes('insur')) {
    return 'Pure Build Construction is fully licensed and insured, and locally owned and operated in Middle Tennessee.';
  }
  return `Good question — the fastest way to a specific answer is a quick call to ${company.phone}, or drop your details in the estimate form and we'll reply the same business day.`;
}
export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [messages, setMessages] = useState<Message[]>([{
    id: 1,
    from: 'bot',
    text: 'Hi! How can we help with your project?'
  }]);
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  }, [messages, typing, open]);
  const send = (text: string) => {
    const value = text.trim();
    if (!value) return;
    setMessages(m => [...m, {
      id: Date.now(),
      from: 'user',
      text: value
    }]);
    setInput('');
    setTyping(true);
    window.setTimeout(() => {
      setTyping(false);
      setMessages(m => [...m, {
        id: Date.now() + 1,
        from: 'bot',
        text: replyFor(value)
      }]);
    }, 850);
  };
  return <>
      <motion.button type="button" onClick={() => setOpen(v => !v)} aria-label={open ? 'Close chat' : 'Open chat'} aria-expanded={open} whileTap={{
      scale: 0.94
    }} className="fixed bottom-6 right-5 z-[65] inline-flex items-center justify-center rounded-full bg-brand-500 text-white shadow-[0_18px_40px_-12px_rgba(31,111,235,0.7)] transition hover:bg-brand-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-500/30 sm:right-8" style={{
      height: 60,
      width: 60
    }} data-id="element-43" data-mp="1feo5oiica798">
        <AnimatePresence mode="wait" initial={false} data-id="element-44" data-mp="1z0b3797yvy9i">
          {open ? <motion.span key="x" initial={{
          rotate: -90,
          opacity: 0
        }} animate={{
          rotate: 0,
          opacity: 1
        }} exit={{
          rotate: 90,
          opacity: 0
        }} data-id="element-45" data-mp="1uzr625ne8und">
              <X className="h-6 w-6" data-id="element-46" data-mp="1l34nxomgchp1" />
            </motion.span> : <motion.span key="chat" initial={{
          rotate: 90,
          opacity: 0
        }} animate={{
          rotate: 0,
          opacity: 1
        }} exit={{
          rotate: -90,
          opacity: 0
        }} data-id="element-47" data-mp="1l57np0depp6y">
              <MessageSquare className="h-6 w-6" data-id="element-48" data-mp="3fhxg1do4mqc8" />
            </motion.span>}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence data-id="element-49" data-mp="3n05489lwkm5u">
        {open && <motion.aside key="chat-panel" initial={{
        opacity: 0,
        y: 20,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.96
      }} transition={{
        duration: 0.28,
        ease: [0.22, 1, 0.36, 1]
      }} role="dialog" aria-label="Chat with Pure Build Construction" className="fixed bottom-28 right-4 z-[65] flex h-[min(560px,72vh)] w-[calc(100vw-2rem)] max-w-[400px] flex-col overflow-hidden bg-white shadow-[0_30px_80px_-20px_rgba(5,13,24,0.55)] sm:right-8" data-id="element-50" data-mp="3ryztnuedl8yx">
            <div className="flex items-center gap-3 bg-navy-900 px-6 py-5" data-id="element-51" data-mp="3iyjwf2o86y2i">
              <span className="inline-flex h-10 w-10 items-center justify-center bg-brand-500 text-white" data-id="element-52" data-mp="3iwgvhnljbkj0">
                <MessageSquare className="h-4 w-4" aria-hidden="true" data-id="element-53" data-mp="y8kp8vf08t0y" />
              </span>
              <div className="min-w-0" data-id="element-54" data-mp="yafc4f9u6v7o">
                <p className="truncate font-heading text-[13px] font-extrabold uppercase tracking-[0.14em] text-white" data-id="element-55" data-mp="2uaxrpj30w9to">
                  Pure Build Construction
                </p>
                <p className="mt-1 flex items-center gap-2 text-xs text-white/45" data-id="element-56" data-mp="33cxcmmh5ry1w">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-400" aria-hidden="true" data-id="element-57" data-mp="2zatx9mrdc9bc" />
                  Typically replies in a few minutes
                </p>
              </div>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto bg-mist px-5 py-5" data-id="element-58" data-mp="26mem5bbcyb5a">
              {messages.map(m => <div key={m.id} className={m.from === 'user' ? 'flex justify-end' : 'flex justify-start'} data-id="element-59" data-mp="2kdt5jj96gecv">
                  <p className={['max-w-[85%] px-4 py-3 text-sm leading-relaxed', m.from === 'user' ? 'bg-brand-500 text-white' : 'border-l-2 border-brand-500 bg-white text-navy'].join(' ')} data-id="element-60" data-mp="2flrx68f4r7s4">
                    {m.text}
                  </p>
                </div>)}

              {typing && <div className="flex justify-start" aria-live="polite" data-id="element-61" data-mp="13tkbeb236du5">
                  <span className="flex gap-1.5 border-l-2 border-brand-500 bg-white px-4 py-4" data-id="element-62" data-mp="13rfv0tn03mcz">
                    {[0, 1, 2].map(i => <motion.span key={i} className="h-1.5 w-1.5 rounded-full bg-navy/30" animate={{
                opacity: [0.25, 1, 0.25]
              }} transition={{
                duration: 1,
                repeat: Infinity,
                delay: i * 0.15
              }} data-id="element-63" data-mp="1whoin10b2yyy" />)}
                  </span>
                </div>}

              {messages.length === 1 && !typing && <div className="space-y-2 pt-3" data-id="element-64" data-mp="b3k3e3cagkn2">
                  {quickReplies.map(q => <button key={q} type="button" onClick={() => send(q)} className="block w-full border border-mist-200 bg-white px-4 py-3 text-left text-sm font-semibold text-navy transition hover:border-brand-500 hover:bg-brand-500 hover:text-white" data-id="element-65" data-mp="g59f7l7c52c7">
                      {q}
                    </button>)}
                </div>}
              <div ref={endRef} data-id="element-66" data-mp="g2lml2ne9ig6" />
            </div>

            <form onSubmit={e => {
          e.preventDefault();
          send(input);
        }} className="flex items-center gap-2 border-t border-mist-200 bg-white px-4 py-4" data-id="element-67" data-mp="b4ttw6exdsax">
              <label htmlFor="chat-input" className="sr-only" data-id="element-68" data-mp="2bgwar5x22ii">
                Type your message
              </label>
              <input id="chat-input" value={input} onChange={e => setInput(e.target.value)} placeholder="Type your message..." className="h-11 flex-1 border border-mist-200 px-4 text-sm text-navy outline-none transition placeholder:text-mist-400 focus:border-brand-500" data-id="element-69" data-mp="yvz9ckgyeikp" />
              <button type="submit" aria-label="Send message" disabled={!input.trim()} className="inline-flex h-11 w-11 shrink-0 items-center justify-center bg-brand-500 text-white transition hover:bg-brand-600 disabled:opacity-40" data-id="element-70" data-mp="2lquov1znrwqm">
                <Send className="h-4 w-4" aria-hidden="true" data-id="element-71" data-mp="30nd5j2qj38x4" />
              </button>
            </form>

            <a href={company.phoneHref} className="flex items-center justify-center gap-2 bg-navy-900 py-3.5 text-[11px] font-bold uppercase tracking-[0.16em] text-white transition hover:bg-navy-800" data-id="element-72" data-mp="35gejn8iwv4fn">
              <Phone className="h-3.5 w-3.5 text-brand-400" aria-hidden="true" data-id="element-73" data-mp="3edyr4z7d9wyp" />
              Rather talk? Call {company.phone}
            </a>
          </motion.aside>}
      </AnimatePresence>
    </>;
}